import {
  DateValue,
  EAuctionBiddingEngine,
  EAuctionBiddingStatus,
  EAuctionStatus,
  EAuctionType,
  EBrand,
  ELotStatus,
} from '@bonhams1793/bonhams-typescript'
import { useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { useStore } from '@nx/app-store'
import {
  ActionButtons,
  ChannelState,
  FinishedLotStatus,
  UserLot,
  buildLotChannelName,
} from '@nx/bidding-engine'
import { usePusher } from '@nx/external/pusher'
import { canAuctionBeRegisteredFor, canLotBeBidOn } from '@nx/helpers'

import { BiddingWidget } from '@web/components/bidding/BiddingWidget/BiddingWidget'
import { useGetUser, useGetUserAddresses } from '@web/helpers/api'
import { useUserRegistrations } from '@web/helpers/api/user/useUserRegistrations'

import { StyledBiddingWidgetWrapper } from './LotCardBiddingWidget.styles'

export function LotCardBiddingWidget({
  auction,
  lot,
  showActionButtons,
  showBidding,
  showBidModal,
  showLoginModal,
  showRegisterModal,
  isAddressInRegistrationModalEnabled = false,
}: LotCardBiddingWidgetProps) {
  const [refreshCounter, setRefreshCounter] = useState(0)

  const { data: user } = useGetUser()
  const { data: userAddresses } = useGetUserAddresses({
    clientNo: user?.customerId,
  })
  const { userRegistrations } = useUserRegistrations()
  const userLotData: UserLot | undefined = useStore(
    useShallow((store) => store.user.lots[lot.lotUniqueId])
  )

  const [lotFinishedData, setLotFinishedData] = useState<{
    hammerPrice: number | null
    hammerPremium: number | null
    status: ELotStatus
  } | null>(null)
  const { pusherConnectionStatus } = usePusher()
  const channelName = buildLotChannelName(lot.lotUniqueId)
  const channel: ChannelState | undefined = useStore(
    useShallow((store) => store.channels[channelName])
  )

  const auctionCanBeBidOn = canAuctionBeRegisteredFor({
    auctionType: auction.auctionType,
    auctionStatus: auction.auctionStatus,
    biddingStatus: auction.biddingStatus,
    biddingEngine: lot.biddingEngine,
  })

  const lotCanBeBidOn = canLotBeBidOn({
    auctionType: auction.auctionType,
    biddableFrom: auction.startDate,
    biddingEngine: lot.biddingEngine,
    isExhibition: lot.isExhibition,
    isPreview: lot.isPreviewLot,
    isToBeSold: lotFinishedData ? false : lot.isToBeSold,
  })

  return auctionCanBeBidOn && lotCanBeBidOn ? (
    showBidding || showActionButtons ? (
      <StyledBiddingWidgetWrapper>
        {showBidding ? (
          <BiddingWidget
            key={`${lot.lotUniqueId}-${refreshCounter}`}
            auction={{
              auctionId: auction.auctionId,
              startDate: auction.startDate,
              biddingStatus: auction.biddingStatus,
              auctionStatus: auction.auctionStatus,
              auctionType: auction.auctionType,
            }}
            biddingStyle="nextIncrement"
            lot={{
              currencySymbol: lot.currencySymbol,
              hammerTime: lot.hammerTime,
              isExhibition: lot.isExhibition,
              isPreviewLot: lot.isPreviewLot,
              isToBeSold: lot.isToBeSold,
              lotUniqueId: lot.lotUniqueId,
            }}
            timerStyle="text"
            onLotFinished={setLotFinishedData}
            refreshCallback={() => setRefreshCounter((state) => state + 1)}
          />
        ) : null}

        {showActionButtons ? (
          <ActionButtons
            isLoggedIn={!!user}
            showLoginModal={showLoginModal}
            showRegisterModal={showRegisterModal}
            showBidModal={showBidModal}
            userHasAnAddress={
              typeof userAddresses !== 'undefined'
                ? !!userAddresses.length
                : undefined
            }
            userRegistrations={userRegistrations}
            userHasBids={userLotData?.hasActiveBid}
            lot={{
              auctionId: String(auction.auctionId),
              auctionStatus: auction.auctionStatus,
              auctionType: auction.auctionType,
              biddableFrom: auction.startDate,
              biddingStatus: auction.biddingStatus,
              isExhibition: lot.isExhibition,
              isPreview: lot.isPreviewLot,
              isToBeSold: lot.isToBeSold,
              biddingEngine: lot.biddingEngine,
            }}
            isAddressInRegistrationModalEnabled={
              isAddressInRegistrationModalEnabled
            }
            hideBiddingButtons={Boolean(
              channel?.subscriptionStatus !== 'SUBSCRIBED' ||
                pusherConnectionStatus === 'DISCONNECTED'
            )}
          />
        ) : null}
      </StyledBiddingWidgetWrapper>
    ) : null
  ) : !lot.isToBeSold || !!lotFinishedData ? (
    <StyledBiddingWidgetWrapper>
      <FinishedLotStatus
        lotStatus={lotFinishedData?.status ?? lot.status}
        hammerPrice={lotFinishedData?.hammerPrice ?? lot.hammerPrice}
        hammerPremium={lotFinishedData?.hammerPremium ?? lot.hammerPremium}
        currencySymbol={lot.currencySymbol}
        isUserWinner={!!userLotData?.won}
        invoiceId={
          userLotData?.invoiceId ? String(userLotData.invoiceId) : undefined
        }
        isResultPublished={lot.isResultPublished || !!lotFinishedData}
      />
    </StyledBiddingWidgetWrapper>
  ) : null
}

export interface LotCardBiddingWidgetProps {
  auction: {
    auctionId: string
    startDate?: DateValue
    biddingStatus: EAuctionBiddingStatus
    auctionStatus: EAuctionStatus
    auctionType: EAuctionType
  }
  lot: {
    biddingEngine: EAuctionBiddingEngine
    brand: EBrand
    currencySymbol: string
    hammerPremium: number
    hammerPrice: number
    hammerTime?: DateValue
    isExhibition: boolean
    isPreviewLot: boolean
    isResultPublished: boolean
    isToBeSold: boolean
    lotNo: string
    lotUniqueId: string
    status: ELotStatus
  }
  showActionButtons: boolean
  showBidding: boolean
  showBidModal: () => void
  showLoginModal: () => void
  showRegisterModal: () => void
  isAddressInRegistrationModalEnabled?: boolean
}
